import SharedPreferences from "../../../constants/SharedPreferences";
import {
  getErrorMessage,
  getErrorMessageFromCaughtError,
  generateBCryptSalt,
  getEncryptionCurveFromStorage,
  getFromStorage,
  getSigningCurveFromStorage,
  saveInStorage,
  logApiResponse,
} from "../../../util/Utility";
import { getNonAuthRestClient } from "../../RestUtility";
import { aeadEncryptWithPasscode } from "../../../crypto/AuthenticatedEncryption";
import {
  exportEccPrivateKey,
  exportEccPublicKey,
  generateEccKeyPair,
  generateEccSigningKeyPair,
} from "../../../crypto/EccEncryption";
import BayunError from "../../../constants/BayunError";
import {
  appendSignatureAndMetadata,
  signData,
} from "../../../util/SignatureUtil";
import { derivePbkdf2Encoded } from "../../../crypto/PBKDF2";
import SigningPublicKeyTags from "../../../constants/SigningPublicKeyTags";
import Constants from "../../../constants/Constants";

async function ensureMemberSigningPrivateKeyForCreateMemberApp(
  sessionId,
  orgName,
  orgMemberId,
  passcode,
  appId,
  appSecret,
  appKeyPairId,
  memberAuthSalt,
) {
  let memberSigningPrivateKey = await getFromStorage(
    sessionId,
    SharedPreferences.MEMBER_SIGNING_PRIVATE_KEY,
  );
  if (memberSigningPrivateKey) return memberSigningPrivateKey;

  let existingMemberAppId = await getFromStorage(
    sessionId,
    SharedPreferences.MEMBER_APP_ID,
  );
  if (!existingMemberAppId && passcode && appSecret && memberAuthSalt) {
    await saveInStorage(
      sessionId,
      SharedPreferences.MEMBER_AUTH_SALT,
      memberAuthSalt,
    );
    const { authenticateWithCredentials } = await import("../authApi");
    let bootstrapFailure = null;
    try {
      await authenticateWithCredentials(
        sessionId,
        orgName,
        orgMemberId,
        false,
        false,
        (error) => {
          bootstrapFailure = error;
        },
      );
    } catch (error) {
      throw (
        bootstrapFailure ?? getErrorMessageFromCaughtError(sessionId, error)
      );
    }
    existingMemberAppId = await getFromStorage(
      sessionId,
      SharedPreferences.MEMBER_APP_ID,
    );
  }

  if (existingMemberAppId && passcode && appSecret && memberAuthSalt) {
    await saveInStorage(
      sessionId,
      SharedPreferences.MEMBER_AUTH_SALT,
      memberAuthSalt,
    );
    const { getMemberLockBox, validatePasscode } = await import("../memberApi");
    let validated = false;
    try {
      validated = await validatePasscode(
        sessionId,
        appId,
        appSecret,
        appKeyPairId,
        orgName,
        orgMemberId,
        passcode,
        null,
      );
    } catch (error) {
      throw getErrorMessageFromCaughtError(sessionId, error);
    }
    if (!validated) {
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    }
    await getMemberLockBox(sessionId, orgMemberId);
    memberSigningPrivateKey = await getFromStorage(
      sessionId,
      SharedPreferences.MEMBER_SIGNING_PRIVATE_KEY,
    );
  }

  if (!memberSigningPrivateKey) {
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }
  return memberSigningPrivateKey;
}

/**
 * This function creates a new memberApp for linking from admin panel.
 *
 * @param {String} sessionId id of the session in question.
 * @param {String} orgName the name of the org for the new memberApp.
 * @param {String} orgMemberId the member if for the new memberApp.
 * @param {String} passcode the passcode to encrypt the memberAppPrivateKey.
 * @param {String} memberKeySalt key salt of the member.
 * @param {String} appId It is to be provided by the developer for all API calls to LMS.
 * @param {String} appSecret It is to be provided by the developer for all API calls to LMS.
 * @param {String} appKeyPairId It is to be provided by the developer for all API calls to LMS.
 */
export async function createMemberAppKeyPair(
  sessionId,
  orgName,
  orgMemberId,
  passcode,
  memberAuthSalt,
  memberKeySalt,
  appId,
  appSecret,
  appKeyPairId,
) {
  if (!memberAuthSalt && !memberKeySalt) {
    memberAuthSalt = generateBCryptSalt();
    memberKeySalt = generateBCryptSalt();
    await saveInStorage(
      sessionId,
      SharedPreferences.MEMBER_AUTH_SALT,
      memberAuthSalt,
    );
  }

  // create the authPasscodeHash

  const authPasscodeHash = await derivePbkdf2Encoded(passcode, memberAuthSalt);

  const encryptionCurve = await getEncryptionCurveFromStorage(sessionId);
  const signingCurve = await getSigningCurveFromStorage(sessionId);
  const memberAppPublicKeyContext =
    `memberAppPublicKey$${orgName}${orgMemberId}${appId}`;

  const memberAppKeyPair = await generateEccKeyPair(encryptionCurve);

  // save the memberAppPrivateKey
  const memberAppPrivateKey = await exportEccPrivateKey(
    memberAppKeyPair.privateKey,
  );
  const memberAppPublicKey = await exportEccPublicKey(
    memberAppKeyPair.publicKey,
  );
  const memberAppSigningKeyPair =
    await generateEccSigningKeyPair(signingCurve);
  const memberAppSigningPrivateKey = await exportEccPrivateKey(
    memberAppSigningKeyPair.privateKey,
  );
  const memberAppSigningPublicKey = await exportEccPublicKey(
    memberAppSigningKeyPair.publicKey,
  );

  await saveInStorage(
    sessionId,
    SharedPreferences.MEMBER_APP_PUBLIC_KEY,
    memberAppPublicKey,
  );
  await saveInStorage(
    sessionId,
    SharedPreferences.MEMBER_APP_SIGNING_PUBLIC_KEY,
    memberAppSigningPublicKey,
  );
  await saveInStorage(
    sessionId,
    SharedPreferences.MEMBER_APP_SIGNING_PRIVATE_KEY,
    memberAppSigningPrivateKey,
  );

  // encrypt the memberApp private key with passcode
  const encryptedMemberAppPrivateKey = await aeadEncryptWithPasscode(
    memberAppPrivateKey,
    passcode,
    memberKeySalt,
    orgMemberId,
  );

  // The signing private key is sealed with the same passcode-derived key as the
  // encryption private key; sending it as a raw PEM would let the server forge
  // member-app signatures.
  const encryptedMemberAppSigningPrivateKey = await aeadEncryptWithPasscode(
    memberAppSigningPrivateKey,
    passcode,
    memberKeySalt,
    orgMemberId,
  );

  if (!encryptedMemberAppSigningPrivateKey) {
    console.log("MemberAppSigningPrivateKey encryption failed.");
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }

  // First layer: member-app signing key attests the encryption public key.
  const signedMemberAppPublicKey = await signData(
    memberAppPublicKey,
    memberAppSigningPrivateKey,
    memberAppPublicKeyContext,
    signingCurve,
    sessionId,
  );

  if (!signedMemberAppPublicKey) {
    console.log("MemberAppPublicKey signature could not be generated.");
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }

  let finalSignedMemberAppPublicKey = appendSignatureAndMetadata(
    memberAppPublicKey,
    signedMemberAppPublicKey,
    SigningPublicKeyTags.MEMBER_APP_SIGNING_PUBLIC_KEY_TAG,
    memberAppPublicKeyContext,
  );

  const memberSigningPrivateKey =
    await ensureMemberSigningPrivateKeyForCreateMemberApp(
      sessionId,
      orgName,
      orgMemberId,
      passcode,
      appId,
      appSecret,
      appKeyPairId,
      memberAuthSalt,
    );
  const memberAppPublicKeyContextLayer2 =
    `${memberAppPublicKeyContext}${Constants.CONTEXT_SEPARATOR}${signedMemberAppPublicKey}`;
  const signedMemberAppPublicKeyLayer2 = await signData(
    memberAppPublicKey,
    memberSigningPrivateKey,
    memberAppPublicKeyContextLayer2,
    signingCurve,
    sessionId,
  );
  if (!signedMemberAppPublicKeyLayer2) {
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }
  finalSignedMemberAppPublicKey = appendSignatureAndMetadata(
    finalSignedMemberAppPublicKey,
    signedMemberAppPublicKeyLayer2,
    SigningPublicKeyTags.MEMBER_SIGNING_PUBLIC_KEY_TAG,
    memberAppPublicKeyContextLayer2,
  );

  // create a request object
  const requestBody = {
    authPasscodeHash,
    orgMemberId,
    orgName,
    memberAppKeyPairRequest: {
      privateKey: encryptedMemberAppPrivateKey,
      publicKey: finalSignedMemberAppPublicKey,
      signingPublicKey: memberAppSigningPublicKey,
      signingPrivateKey: encryptedMemberAppSigningPrivateKey,
    },
    memberAuthSalt,
    memberKeySalt,
  };

  // create a rest client
  const nonAuthRestClient = getNonAuthRestClient(appId, appKeyPairId);

  try {
    // call the createMemberApp API
    const createMemberAppResponse = await nonAuthRestClient.post(
      "/createMemberApp",
      requestBody,
    );

    // console.log(createMemberAppResponse);
    await logApiResponse("createMemberAppResponse", createMemberAppResponse);
  } catch (error) {
    console.log(error);
    console.log(error?.response?.data ?? error);
    throw getErrorMessage(sessionId, error?.response?.data ?? error);
  }
}
